import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Select, Radio, Checkbox, Spin, Button, message, Popconfirm } from "antd";
import authApi from "../../api/authApi";
import ReferrerPreviewPanel, { type ReferrerPreview } from "./referrer-preview-panel";

export type ReferrerEditTarget = {
    userId: number;
    name?: string | null;
    email?: string | null;
    ref?: number | null;
};

type Props = {
    /** null = ẩn modal */
    target: ReferrerEditTarget | null;
    onClose: () => void;
    onSuccess: () => void;
};

type LookupOption = { value: number; label: string };

const BACKFILL_OPTIONS = [
    { value: "none", label: "Không áp dụng đơn cũ — chỉ đơn phát sinh về sau" },
    { value: "unpaid", label: "Đơn chưa thanh toán — cron trả tiền như bình thường (khuyến nghị)" },
    { value: "all", label: "Cả đơn đã thanh toán — cộng bù thẳng vào ví người giới thiệu" },
];

/**
 * Gán / đổi / gỡ người giới thiệu cho một user.
 *
 * Dùng chung cho trang /user (ca user quên nhập mã mời — chỉ ở đây mới tìm được
 * user chưa có người giới thiệu) và trang /referred-users (sửa cặp đã có).
 */
function ReferrerEditModal({ target, onClose, onSuccess }: Props) {
    const [options, setOptions] = useState<LookupOption[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedRefId, setSelectedRefId] = useState<number | null>(null);
    const [preview, setPreview] = useState<ReferrerPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [backfillMode, setBackfillMode] = useState("unpaid");
    const [notify, setNotify] = useState(false);
    const [saving, setSaving] = useState(false);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reset toàn bộ form mỗi lần mở cho user khác.
    useEffect(() => {
        if (!target) return;
        setOptions([]);
        setSelectedRefId(null);
        setPreview(null);
        setBackfillMode("unpaid");
        setNotify(false);
    }, [target?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPreview = useCallback(
        async (userId: number, refId: number | null) => {
            setPreviewLoading(true);
            try {
                const { data } = await authApi.previewReferrerChange(userId, refId);
                setPreview(data.data ?? data);
            } catch (error: any) {
                message.error(error.response?.data?.message || "Không tải được dữ liệu xem trước");
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        },
        []
    );

    // Mở modal -> nạp trạng thái hiện tại; đổi người giới thiệu -> nạp lại tác động.
    useEffect(() => {
        if (target) loadPreview(target.userId, selectedRefId);
    }, [target?.userId, selectedRefId, loadPreview]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (value: string) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const q = value.trim();
        if (!q) { setOptions([]); return; }
        searchTimer.current = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await authApi.lookupReferrer(q);
                const users = (data.data ?? data).users || [];
                setOptions(
                    users.map((u: any) => ({
                        value: u.userId,
                        label: `#${u.userId} · ${u.name || "(chưa đặt tên)"} · ${u.email || "—"}`,
                    }))
                );
            } catch {
                setOptions([]);
            } finally {
                setSearching(false);
            }
        }, 350);
    };

    const submit = async (refId: number | null) => {
        if (!target) return;
        setSaving(true);
        try {
            const { data } = await authApi.updateUserReferrer(target.userId, {
                refId,
                backfillMode: refId == null ? "none" : backfillMode,
                notify: refId == null ? false : notify,
            });
            const applied = (data.data ?? data).applied;
            message.success(
                refId == null
                    ? "Đã gỡ người giới thiệu"
                    : `Đã lưu người giới thiệu · backfill ${applied.unpaidOrders + applied.paidOrders} đơn`
            );
            onSuccess();
            onClose();
        } catch (error: any) {
            message.error(error.response?.data?.message || "Không lưu được thay đổi");
        } finally {
            setSaving(false);
        }
    };

    const blocked = !preview || preview.blockers.length > 0;
    const hasCurrentRef = Boolean(preview?.currentRef);

    return (
        <Modal
            title="Người giới thiệu"
            open={Boolean(target)}
            onCancel={onClose}
            width={680}
            destroyOnClose
            footer={[
                hasCurrentRef && (
                    <Popconfirm
                        key="remove"
                        title="Gỡ người giới thiệu?"
                        description="Đơn chưa thanh toán sẽ được gỡ khỏi người giới thiệu cũ. Tiền đã trả không bị thu hồi."
                        okText="Gỡ"
                        cancelText="Hủy"
                        onConfirm={() => submit(null)}
                    >
                        <Button danger loading={saving}>Gỡ người giới thiệu</Button>
                    </Popconfirm>
                ),
                <Button key="cancel" onClick={onClose}>Hủy</Button>,
                <Button
                    key="ok"
                    type="primary"
                    loading={saving}
                    disabled={selectedRefId == null || blocked}
                    onClick={() => submit(selectedRefId)}
                >
                    Lưu
                </Button>,
            ].filter(Boolean)}
        >
            <div className="flex flex-col gap-4 py-2">
                <div>
                    <p className="text-xs text-content-3 mb-1">Chọn người giới thiệu (nhập ID, email hoặc tên)</p>
                    <Select
                        showSearch
                        allowClear
                        className="w-full"
                        placeholder="VD: 12345 hoặc email@gmail.com"
                        filterOption={false}
                        onSearch={handleSearch}
                        onChange={(v) => setSelectedRefId(v ?? null)}
                        value={selectedRefId ?? undefined}
                        options={options}
                        notFoundContent={searching ? <Spin size="small" /> : null}
                    />
                </div>

                {previewLoading ? (
                    <div className="py-6 text-center"><Spin /></div>
                ) : (
                    preview && <ReferrerPreviewPanel preview={preview} />
                )}

                {selectedRefId != null && (
                    <div className="flex flex-col gap-2">
                        <p className="text-xs text-content-3 m-0">Áp dụng hoa hồng cho đơn cũ</p>
                        <Radio.Group
                            value={backfillMode}
                            onChange={(e) => setBackfillMode(e.target.value)}
                            className="flex flex-col gap-1"
                        >
                            {BACKFILL_OPTIONS.map((opt) => (
                                <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                            ))}
                        </Radio.Group>
                        <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
                            Gửi thông báo &quot;giới thiệu thành công&quot; cho người giới thiệu
                        </Checkbox>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default ReferrerEditModal;
